/** @file bookings/payload.js — buildBookingPayload (module-internal).
 *
 *  Refactor R-7 (2026-07-14): the per-column coercion table used to
 *  live inline here (~100 lines of `v.x !== "" ? parseInt(v.x, 10) : null`
 *  boilerplate). It now lives in `/lib/booking-fields.js` as the
 *  single source of truth shared with `bookingDefaults()` and
 *  `buildInitialValues()`. Adding a new column → one Map entry,
 *  three consumers stay in sync automatically.
 */
import { nullEmpty } from "@/lib/constants";
import { composeInSiteTz } from "@/lib/datetime";
import { supabase } from "@/lib/supabaseClient";
import { deriveCoercion } from "@/lib/booking-fields";

export async function buildBookingPayload(v, currentUserId, isInsert) {
  void currentUserId; // DB trigger fills owner_user_id / created_by_user_id

  // Compose scheduled_start / scheduled_end in the site timezone. This
  // is the one form → DB transform that can't live in the Map because
  // it depends on the site's IANA tz + three form fields at once
  // (booking_date / start_time / end_time).
  const tz = v._booking_tz || null;
  const [yy, mo, dd] = (v.booking_date || "").split("-").map((n) => parseInt(n, 10));
  const [sh, sm]    = (v.start_time   || "00:00").split(":").map((n) => parseInt(n, 10));
  const [eh, em]    = (v.end_time     || "00:00").split(":").map((n) => parseInt(n, 10));
  const scheduled_start = tz && yy
    ? composeInSiteTz({ year: yy, month: mo, day: dd, hour: sh, minute: sm }, tz)
    : new Date(`${v.booking_date}T${v.start_time}:00`).toISOString();
  const scheduled_end   = tz && yy
    ? composeInSiteTz({ year: yy, month: mo, day: dd, hour: eh, minute: em }, tz)
    : new Date(`${v.booking_date}T${v.end_time}:00`).toISOString();

  // Every flat column + JSONB parent (vehicle_regos, mass_limits,
  // load_restraint, trailer_weights) comes from the single source-of-truth
  // Map. `derived` is a flat object of DB-shaped values, coerced but not
  // yet passed through `nullEmpty()`.
  const derived = deriveCoercion(v);

  // vehicle_regos / mass_limits / trailer_weights: legacy code wrapped
  // these in nullEmpty() so any "" child collapsed to null. The Map's
  // `str` coerce is a passthrough for these so we keep the wrap here.
  // load_restraint uses `orNull` for `method` and pure booleans for the
  // flag children — no nullEmpty needed (matches pre-R7 payload.js).
  const vehicle_regos   = nullEmpty(derived.vehicle_regos);
  const mass_limits     = nullEmpty(derived.mass_limits);
  const load_restraint  = derived.load_restraint;
  const trailer_weights = nullEmpty(derived.trailer_weights);

  // Assemble the final payload. `nullEmpty` on the flat root portion
  // collapses "" strings to null for nullable columns (site_id, title,
  // notes, etc.). JSONB parents are already prepared above.
  const flatRoot = { ...derived };
  delete flatRoot.vehicle_regos;
  delete flatRoot.mass_limits;
  delete flatRoot.load_restraint;
  delete flatRoot.trailer_weights;

  const base = nullEmpty({
    ...flatRoot,
    scheduled_start,
    scheduled_end,
    vehicle_regos,
    mass_limits,
    load_restraint,
    trailer_weights,
  });

  if (isInsert) {
    base.reference_number = "";
    base.status = "approved";
  }
  return base;
}

/* =====================  BOOKING STATUS WORKFLOW  ============= */
